import { useState, useEffect, useRef } from "react";
import * as React from "react";
import PropTypes from "prop-types";
import Switch from "@mui/material/Switch";
import { makeStyles } from "@mui/styles";
import {
  Grid,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { useApp } from "../../AppProvider";
import * as moment from "moment";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import {
  getDashboardData,
  getAllActiveBusiness,
  getDashboardCompareData,
} from "./services/api";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import { ROLES } from "../../sections/sidebar/constants";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import Popper from "@mui/material/Popper";
import ChartDataLabels from "chartjs-plugin-datalabels";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  LinearGraphicColor,
  PieGraphicColor,
  DoughnutGraphicColor,
  DoughnutGraphicColorDark,
  PieGraphicColorDark,
  GetLineGraphicOptions,
  GetDoughnutGraphicOptions,
  GraphicNoDataImg,
} from "./constants/graphics";
import { styled } from "@mui/material/styles";
import { useStyles, useStylesDark } from "./constants/styles";
import { AntSwitch } from "./components/switchStyled";
import {
  formatNumber,
  formatNumberDecimal,
  formatNumberShort,
} from "../../helpers/functions/functions";
import Checkbox from "@mui/material/Checkbox";
import { useForm, Controller, set } from "react-hook-form";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { InputAdornment } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./index.css";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";

moment.locale("es");

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);
// Message Count Component
function CountMessageItem({ label, count, percent, darkMode }) {
  const classes = darkMode ? useStylesDark() : useStyles();

  return (
    <Grid item className={classes.countMessageItemContainer}>
      <Grid item className={classes.countMessageItem}>
        {percent
          ? count.toString().includes(".")
            ? count.toFixed(2)
            : count > 0
              ? count
              : 0
          : formatNumberShort(count)}
        {percent ? "%" : ""}
      </Grid>
      <Grid item className={classes.countMessageItemText}>
        <Typography variant="p">{label}</Typography>
      </Grid>
    </Grid>
  );
}

CountMessageItem.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  percent: PropTypes.bool,
  darkMode: PropTypes.bool.isRequired,
};

const formatProductAnalyticsPeakDay = (value) => {
  if (!value) return "-";

  const parsedDate = moment(
    value,
    ["M/D/YYYY", "MM/DD/YYYY", "D/M/YYYY", "DD/MM/YYYY"],
    true,
  );

  return parsedDate.isValid() ? parsedDate.format("D [de] MMMM") : value;
};
//

const Dashboard = () => {
  const legendMenuRef = useRef(null);
  const [darkMode, setDarkMode] = useState(false);
  const [noData, setNoData] = useState(false);
  const classes = darkMode ? useStylesDark() : useStyles();
  const { showNotification, currentUser } = useApp();
  const initialDashboardBusinessId =
    currentUser.usuarioEmpresas.length > 0
      ? currentUser.usuarioEmpresas[0]["id"]
      : 0;
  const [loading, setLoading] = useState(true);
  const [linearGraphic, setLinearGraphic] = useState({
    labels: [],
    datasets: [],
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [pieGraphic, setPieGraphic] = useState(null);
  const [doughnutGraphic, setDoughnutGraphic] = useState(null);
  const [campaignTypeGraphic, setCampaignTypeGraphic] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openLegend, setOpenLegend] = useState(false);
  const [lineGraphicExternalLegend, setLineGraphicExternalLegend] = useState(
    [],
  );
  const [showCampaignTypeChart, setShowCampaignTypeChart] = useState(false);
  useOutsideAlerter(legendMenuRef);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [dashboardData, setDashboardData] = useState({
    totalSuccessful: 0,
    totalFailed: 0,
    totalProduct: 0,
    totalServiceProvider: {},
    totalDaily: {},
    totalCampaignType: {},
  });
  const {
    handleSubmit,
    resetField,
    register,
    watch,
    control,
    setValue,
    formState: { isValid, errors },
  } = useForm({
    mode: "all",
    defaultValues: {
      now: moment(),
      businessId: initialDashboardBusinessId,
    },
  });
  const { now, businessId } = watch();
  const [business, setBusiness] = useState(0);
  const [businessList, setBusinessList] = useState([]);
  const [activeDashboardBusinessId, setActiveDashboardBusinessId] = useState(
    initialDashboardBusinessId,
  );
  const [firstInit, setFirstInit] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, "d").startOf("M"),
    endDate: moment().subtract(1, "d"),
  });
  const [productAnalyticsDateRange, setProductAnalyticsDateRange] = useState({
    startDate: moment().subtract(30, "days").startOf("day"),
    endDate: moment().subtract(1, "day").endOf("day"),
  });
  const [productAnalyticsRangePicker, setProductAnalyticsRangePicker] =
    useState({
      open: false,
      selectingEnd: false,
    });
  const [productAnalyticsDraftRange, setProductAnalyticsDraftRange] = useState({
    startDate: moment().subtract(30, "days").startOf("day"),
    endDate: moment().subtract(1, "day").endOf("day"),
  });
  const [isProductAnalyticsComparing, setIsProductAnalyticsComparing] =
    useState(false);
  const [productAnalyticsData, setProductAnalyticsData] = useState(() => ({
    total_by_product: [],
    total_sms: { total: 0, variation: 0 },
    diary_average: { total: 0, variation: 0 },
    peak_day: formatProductAnalyticsPeakDay(""),
  }));

  const theme = createTheme({
    components: {
      MuiPickersToolbar: {
        styleOverrides: {
          penIconButton: {
            pointerEvents: "none",
            visibility: "hidden",
          },
        },
      },
    },
  });

  const productAnalyticsMinSelectableDate = moment()
    .subtract(1, "year")
    .startOf("day");
  const productAnalyticsMaxSelectableDate = moment()
    .subtract(1, "day")
    .endOf("day");

  const getClampedProductAnalyticsRange = (range) => {
    const safeStartDate = moment.max(
      moment(range.startDate).startOf("day"),
      moment(productAnalyticsMinSelectableDate).startOf("day"),
    );
    const safeEndDate = moment.min(
      moment(range.endDate).endOf("day"),
      moment(productAnalyticsMaxSelectableDate).endOf("day"),
    );

    return {
      startDate: safeStartDate.isAfter(safeEndDate, "day")
        ? moment(safeEndDate).startOf("day")
        : safeStartDate,
      endDate: safeEndDate.isBefore(safeStartDate, "day")
        ? moment(safeStartDate).endOf("day")
        : safeEndDate,
    };
  };

  const handleOpenProductAnalyticsRangePicker = () => {
    setProductAnalyticsDraftRange(
      getClampedProductAnalyticsRange(productAnalyticsDateRange),
    );
    setProductAnalyticsRangePicker({ open: true, selectingEnd: false });
  };

  const handleCloseProductAnalyticsRangePicker = () => {
    setProductAnalyticsDraftRange(
      getClampedProductAnalyticsRange(productAnalyticsDateRange),
    );
    setProductAnalyticsRangePicker({ open: false, selectingEnd: false });
  };

  const handleApplyProductAnalyticsRangePicker = () => {
    const nextRange = getClampedProductAnalyticsRange(
      productAnalyticsDraftRange,
    );

    setProductAnalyticsDraftRange(nextRange);
    setProductAnalyticsDateRange(nextRange);
    setProductAnalyticsRangePicker({ open: false, selectingEnd: false });
  };

  const handleProductAnalyticsRangeDateChange = (newValue) => {
    if (!newValue) return;

    let nextSelectedDate = moment(newValue).startOf("day");

    if (nextSelectedDate.isBefore(productAnalyticsMinSelectableDate, "day")) {
      nextSelectedDate = moment(productAnalyticsMinSelectableDate).startOf(
        "day",
      );
    }

    if (nextSelectedDate.isAfter(productAnalyticsMaxSelectableDate, "day")) {
      nextSelectedDate = moment(productAnalyticsMaxSelectableDate).startOf(
        "day",
      );
    }

    if (!productAnalyticsRangePicker.selectingEnd) {
      setProductAnalyticsDraftRange({
        startDate: nextSelectedDate,
        endDate: moment(nextSelectedDate).endOf("day"),
      });
      setProductAnalyticsRangePicker({ open: true, selectingEnd: true });
      return;
    }

    const currentStartDate = moment(
      productAnalyticsDraftRange.startDate,
    ).startOf("day");

    if (nextSelectedDate.isBefore(currentStartDate, "day")) {
      setProductAnalyticsDraftRange({
        startDate: nextSelectedDate,
        endDate: moment(nextSelectedDate).endOf("day"),
      });
      setProductAnalyticsRangePicker({ open: true, selectingEnd: true });
      return;
    }

    setProductAnalyticsDraftRange({
      startDate: currentStartDate,
      endDate: moment(nextSelectedDate).endOf("day"),
    });
    setProductAnalyticsRangePicker({ open: true, selectingEnd: false });
  };

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setOpenLegend(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useEffect(async () => {
    if (currentUser.role == ROLES.SUPER) {
      fetchBusinessList();
    }
    prepareData(businessId);
    setFirstInit(true);
  }, []);

  useEffect(async () => {
    if (currentUser.role == ROLES.SUPER && firstInit) {
      prepareData(business);
    }
  }, [business]);

  useEffect(() => {
    if (
      activeDashboardBusinessId === undefined ||
      activeDashboardBusinessId === null
    )
      return;

    fetchProductAnalyticsData(activeDashboardBusinessId);
  }, [
    activeDashboardBusinessId,
    productAnalyticsDateRange.startDate,
    productAnalyticsDateRange.endDate,
  ]);

  const fetchBusinessList = async () => {
    try {
      const response = await getAllActiveBusiness();
      setBusinessList([{ id: 0, razonSocial: "Todas" }].concat(response.data));
    } catch (error) {
      console.log("Error fetching business list", error);
    }
  };

  const formatDashboardApiDate = (value) => moment(value).format("YYYY-M-D");

  const normalizeProductAnalyticsData = (data) => ({
    total_by_product: Array.isArray(data?.total_by_product)
      ? data.total_by_product.map((item) => ({
          name: item?.name || "",
          total: Number(item?.total || 0),
          previous_total: Number(item?.previous_total || 0),
        }))
      : [],
    total_sms: {
      total: Number(data?.total_sms?.total || 0),
      variation: Number(data?.total_sms?.variation || 0),
    },
    diary_average: {
      total: Number(data?.diary_average?.total || 0),
      variation: Number(data?.diary_average?.variation || 0),
    },
    peak_day: {
      total: formatProductAnalyticsPeakDay(data?.peak_day?.total),
      variation: Number(data?.peak_day?.variation || 0),
    },
  });

  const fetchProductAnalyticsData = async (nextBusinessId) => {
    try {
      const { data } = await getDashboardCompareData(
        nextBusinessId,
        formatDashboardApiDate(productAnalyticsDateRange.startDate),
        formatDashboardApiDate(productAnalyticsDateRange.endDate),
      );

      setProductAnalyticsData(normalizeProductAnalyticsData(data));
    } catch (error) {
      console.log("Error fetching dashboard compare data", error);
    }
  };

  const prepareData = async (
    requestedBusinessId = activeDashboardBusinessId,
  ) => {
    const nextBusinessId = requestedBusinessId ?? initialDashboardBusinessId;

    setActiveDashboardBusinessId(nextBusinessId);
    setLoading(true);
    /*const businessIds = currentUser.usuarioEmpresas;
        if(businessIds.length !== 0){
            setValue('businessId', businessIds[0].id)
        }*/
    setDateRange({
      startDate: moment(now).subtract(1, "day").startOf("M"),
      endDate: moment(now).subtract(1, "day"),
    });

    try {
      const { data } = await getDashboardData(
        nextBusinessId,
        formatDashboardApiDate(moment(now).subtract(1, "day").startOf("M")),
        formatDashboardApiDate(moment(now).subtract(1, "day")),
      );
      setData(data);
      getLinearGraphicData(data, darkMode);
      setLoading(false);
    } catch (error) {
      setNoData(true);
      setLoading(false);
    }
  };

  const getLinearGraphicData = (data, darkMode) => {
    const datasets = [];
    let labels;
    let themeColor = {
      linearGraphic: darkMode ? LinearGraphicColor : LinearGraphicColor,
      pirGraphic: darkMode ? PieGraphicColorDark : PieGraphicColor,
      doughnutGraphic: darkMode
        ? DoughnutGraphicColorDark
        : DoughnutGraphicColor,
    };

    Object.entries(data.totalDaily).forEach(([key, value], index) => {
      if (index === 0) {
        labels = value.map((value, index) => index + 1);
      }
      datasets.push({
        data: value,
        labels: key,
        borderColor: themeColor.linearGraphic[index],
        backgroundColor: themeColor.linearGraphic[index],
      });
    });
    setLinearGraphic({ labels, datasets });

    setLineGraphicExternalLegend(
      Object.entries(data.totalProduct).map(([key, value], index) => {
        return {
          label: key,
          color: darkMode
            ? LinearGraphicColor[index]
            : LinearGraphicColor[index],
          id: index,
        };
      }),
    );

    setPieGraphic({
      labels: ["Movistar", "Digitel", "Movilnet"],
      datasets: [
        {
          data: [
            data.totalServiceProvider.MOVISTAR
              ? data.totalServiceProvider.MOVISTAR.amount
              : 0,
            data.totalServiceProvider.DIGITEL
              ? data.totalServiceProvider.DIGITEL.amount
              : 0,
            data.totalServiceProvider.MOVILNET
              ? data.totalServiceProvider.MOVILNET.amount
              : 0,
          ],
          backgroundColor: themeColor.pirGraphic,
          borderColor: themeColor.pirGraphic,
          hoverBackgroundColor: themeColor.pirGraphic,
          hoverBorderWidth: 1,
          borderWidth: 0,
        },
      ],
    });
    setDoughnutGraphic({
      labels: ["sms exitosos", "sms fallidos"],
      datasets: [
        {
          data: [data.totalSuccessful, data.totalFailed],
          backgroundColor: themeColor.doughnutGraphic,
          borderColor: themeColor.doughnutGraphic,
          hoverBackgroundColor: themeColor.doughnutGraphic,
          hoverBorderWidth: 1,

          borderWidth: 0,
        },
      ],
    });

    // Campaign Type Chart (Normal vs Custom)
    const campaignTypeColors = ["#1BC3DD", "#D67A63"];
    setCampaignTypeGraphic({
      labels: ["Campañas Normales", "Campañas Personalizadas"],
      datasets: [
        {
          data: [
            data.totalCampaignType?.normal || 0,
            data.totalCampaignType?.custom || 0,
          ],
          backgroundColor: campaignTypeColors,
          borderColor: "white",
          borderWidth: 2,
        },
      ],
    });
  };

  const changeMode = (event) => {
    setDarkMode(event.target.checked);
    getLinearGraphicData(dashboardData, event.target.checked);
  };

  const setData = (data) => {
    setDashboardData({
      totalSuccessful: data.totalSuccessful,
      totalFailed: data.totalFailed,
      totalServiceProvider: data.totalServiceProvider,
      totalProduct: data.totalProduct,
      totalDaily: data.totalDaily,
      totalCampaignType: data.totalCampaignType || { normal: 0, custom: 0 },
    });
    if (data.totalSuccessful + data.totalFailed === 0) {
      setNoData(true);
    } else {
      setNoData(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenLegend(!openLegend);
  };

  const getProgressBarPercent = (products, productCount) => {
    const counts = Object.entries(products).map(
      ([key, value]) => value.successful + value.failed,
    );

    const maxCount = Math.max(...counts);

    return `${(productCount * 100) / maxCount}%`;
  };

  const textCenter = {
    id: "textCenter",
    afterDatasetsDraw(chart, args, pluginOptions) {
      const { ctx, data } = chart;
      const color =
        data.datasets[0].backgroundColor[0] === "#1cc3dd"
          ? "#FFFFFF"
          : "#000000";
      ctx.font = `bolder ${windowWidth > 1500 ? "40px" : "30px"} sans-serif`;
      ctx.textAlign = "center";
      ctx.fillStyle = color;
      ctx.textBaseline = "middle";
      ctx.fillText(
        `${formatNumberShort(
          data.datasets[0].data[0] + data.datasets[0].data[1],
        )}`,
        chart.getDatasetMeta(0).data[0].x,
        chart.getDatasetMeta(0).data[0].y,
      );
      ctx.font = "normal 12px sans-serif";
      ctx.fillText(
        "enviados",
        chart.getDatasetMeta(0).data[0].x,
        chart.getDatasetMeta(0).data[0].y + 20,
      );
    },
  };

  const getOrCreateLegendList = (chart, id) => {
    const legendContainer = document.getElementById(id);
    let listContainer = legendContainer.querySelector("ul");

    if (!listContainer) {
      listContainer = document.createElement("ul");
      listContainer.style.display = "flex";
      listContainer.style.flexDirection = "column";
      listContainer.style.margin = 0;
      listContainer.style.padding = 0;

      legendContainer.appendChild(listContainer);
    }

    return listContainer;
  };

  const handleOption = (lineId) => {
    const id = document.getElementById(lineId);
    id.click();
  };

  const htmlLegendPlugin = {
    id: "htmlLegend",
    afterUpdate(chart, args, options) {
      const ul = getOrCreateLegendList(chart, "js-legend");
      const legend = Object.entries(dashboardData.totalProduct);
      while (ul.firstChild) {
        ul.firstChild.remove();
      }
      const items = chart.options.plugins.legend.labels.generateLabels(chart);
      items.forEach((item, index) => {
        const id = `li-${index}`;

        const li = document.createElement("li");
        li.style.display = "none";
        li.id = id;

        li.onclick = () => {
          chart.setDatasetVisibility(
            item.datasetIndex,
            !chart.isDatasetVisible(item.datasetIndex),
          );
          chart.update();
        };
        ul.appendChild(li);
      });
    },
  };

  const roundedLegendPlugin = {
    id: "roundedLegend",
    beforeDraw(chart) {
      const { ctx, legend } = chart;
      if (!legend || !legend.legendItems) return;

      legend.legendItems.forEach((item, index) => {
        const legendItem = item;
        // Store the original draw function if not already stored
        if (!legendItem._originalDraw) {
          legendItem._originalDraw = true;
        }
      });
    },
    afterDraw(chart) {
      const { ctx, legend, chartArea } = chart;
      if (!legend || !legend.legendItems) return;

      ctx.save();

      legend.legendItems.forEach((item, index) => {
        const x = item.left;
        const y = item.top;
        const boxWidth = legend.options.labels.boxWidth;
        const boxHeight = legend.options.labels.boxHeight;
        const halfHeight = boxHeight / 2;

        // Draw rounded rectangle for legend box
        ctx.fillStyle = item.fillStyle;
        ctx.beginPath();
        ctx.roundRect(
          x,
          y + halfHeight - boxHeight / 2,
          boxWidth,
          boxHeight,
          4,
        );
        ctx.fill();

        // Draw text with matching color
        ctx.fillStyle = item.fontColor || item.fillStyle;
        ctx.font = `500 16px Ubuntu`;
        ctx.textBaseline = "middle";
        ctx.fillText(item.text, x + boxWidth + 10, y + halfHeight);
      });

      ctx.restore();
    },
  };

  const movistarAmount =
    dashboardData.totalServiceProvider?.MOVISTAR?.amount || 0;
  const digitelAmount =
    dashboardData.totalServiceProvider?.DIGITEL?.amount || 0;
  const movilnetAmount =
    dashboardData.totalServiceProvider?.MOVILNET?.amount || 0;
  const totalOperatorMessages = movistarAmount + digitelAmount + movilnetAmount;

  const movistarPercent = totalOperatorMessages
    ? Math.round((movistarAmount * 100) / totalOperatorMessages)
    : 0;
  const digitelPercent = totalOperatorMessages
    ? Math.round((digitelAmount * 100) / totalOperatorMessages)
    : 0;
  const movilnetPercent = totalOperatorMessages
    ? Math.round((movilnetAmount * 100) / totalOperatorMessages)
    : 0;

  const formatCarrierAmount = (value) =>
    value >= 1000 ? `${formatNumber(value)} K` : formatNumber(value);

  const formatVariationLabel = (variation) =>
    `${variation > 0 ? "+" : ""}${variation}%`;

  const productAnalyticsSummaryCards = [
    {
      title: "Total de SMS",
      value: formatNumberShort(productAnalyticsData.total_sms.total),
      variation: productAnalyticsData.total_sms.variation,
      subtitle: "vs período anterior",
    },
    {
      title: "Promedio diario de SMS",
      value: formatNumberDecimal(productAnalyticsData.diary_average.total),
      variation: productAnalyticsData.diary_average.variation,
      subtitle: "variación diaria",
    },
    {
      title: "Día pico",
      value: productAnalyticsData.peak_day.total,
      variation: productAnalyticsData.peak_day.variation,
      subtitle: "Mayor volumen registrado",
    },
  ];

  const productAnalyticsPreviousPeriodColor = "#1BC3DD";
  const productAnalyticsCurrentPeriodColor = "#D67A63";

  const getProductAnalyticsBarDataset = (label, data, backgroundColor) => ({
    label,
    data,
    backgroundColor,
    borderRadius: 0,
    borderSkipped: false,
    hoverBackgroundColor: backgroundColor,
    maxBarThickness: isProductAnalyticsComparing ? 52 : 32,
    categoryPercentage: isProductAnalyticsComparing ? 1 : 0.74,
    barPercentage: isProductAnalyticsComparing ? 1 : 0.82,
    inflateAmount: isProductAnalyticsComparing ? 1 : 0,
  });

  const productAnalyticsBarData = {
    labels: productAnalyticsData.total_by_product.map((item) => item.name),
    datasets: [
      ...(isProductAnalyticsComparing
        ? [
            getProductAnalyticsBarDataset(
              "Periodo anterior",
              productAnalyticsData.total_by_product.map(
                (item) => item.previous_total || 0,
              ),
              darkMode ? "#D9CB67" : productAnalyticsPreviousPeriodColor,
            ),
          ]
        : []),
      getProductAnalyticsBarDataset(
        "Periodo actual",
        productAnalyticsData.total_by_product.map((item) => item.total),
        productAnalyticsCurrentPeriodColor,
      ),
    ],
  };

  const productAnalyticsBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: !darkMode ? "white" : "#4E4E4E",
        bodyColor: !darkMode ? "#000" : "white",
        borderColor: !darkMode ? "#1BC3DD" : "#1BC3DD",
        borderWidth: 1,
        titleColor: !darkMode ? "#000" : "white",
        callbacks: {
          label: (context) =>
            ` ${context.dataset.label}: ${formatNumberShort(context.parsed.y || 0)} SMS`,
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: "rgba(224, 224, 224, 0.5)",
          borderDash: [5, 5],
        },
        border: { display: false },
        ticks: {
          color: darkMode ? "#F8FAFC" : "#475569",
          font: { family: "Ubuntu", size: 12 },
        },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: {
          display: true,
          color: "rgba(224, 224, 224, 0.5)",
          borderDash: [5, 5],
        },
        ticks: {
          color: darkMode ? "#CBD5E1" : "#64748B",
          font: { family: "Ubuntu", size: 12 },
          maxTicksLimit: 4,
          callback: (value) => formatNumberShort(Number(value)),
        },
      },
    },
  };

  const productAnalyticsDateRangeLabel = `${moment(
    productAnalyticsDateRange.startDate,
  ).format("D [de] MMMM [de] YYYY")} - ${moment(
    productAnalyticsDateRange.endDate,
  ).format("D [de] MMMM [de] YYYY")}`;

  const renderProductAnalyticsRangeDay = (
    day,
    _selectedDates,
    pickersDayProps,
  ) => {
    const currentDay = moment(day).startOf("day");
    const startDate = moment(productAnalyticsDraftRange.startDate).startOf(
      "day",
    );
    const endDate = moment(productAnalyticsDraftRange.endDate).startOf("day");
    const isStart = currentDay.isSame(startDate, "day");
    const isEnd = currentDay.isSame(endDate, "day");
    const isInRange =
      currentDay.isAfter(startDate, "day") &&
      currentDay.isBefore(endDate, "day");
    const shouldHighlightRange =
      !pickersDayProps.outsideCurrentMonth &&
      !pickersDayProps.disabled &&
      (isStart || isEnd || isInRange);

    return (
      <Box
        sx={{
          backgroundColor: shouldHighlightRange
            ? "rgba(27, 195, 221, 0.16)"
            : "transparent",
          borderTopLeftRadius: isStart ? "999px" : 0,
          borderBottomLeftRadius: isStart ? "999px" : 0,
          borderTopRightRadius: isEnd ? "999px" : 0,
          borderBottomRightRadius: isEnd ? "999px" : 0,
        }}
      >
        <PickersDay
          {...pickersDayProps}
          day={day}
          disableMargin
          sx={{
            fontFamily: "Open Sans !important",
            ...(isStart || isEnd
              ? {
                  backgroundColor: "#1BC3DD",
                  color: "#FFFFFF",
                  "&:hover, &:focus": {
                    backgroundColor: "#1fabc0",
                  },
                }
              : {}),
            ...(isInRange
              ? {
                  color: "#0F172A",
                }
              : {}),
          }}
        />
      </Box>
    );
  };

  return (
    <Grid className={classes.container}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        className={classes.dashboardHeaderContainer}
      >
        <Grid item>
          <Typography variant="h3" className={classes.dashboardHeaderTitle}>
            Dashboard
          </Typography>
          <Typography variant="p" className={classes.dashboardHeaderSubTitle}>
            {" "}
            <b>Visualiza la evolución</b> de tus envíos de{" "}
            <b>mensajes vía SMS acumulado del mes</b>
          </Typography>
        </Grid>
        {/*          <form  onSubmit={handleSubmit()} style={{width:'200px'}}>
            <ThemeProvider theme={theme}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Grid item sx={{width:"100%"}}>
                    <Controller 
                        name="now" 
                        control={control} 
                        render={({field, fieldState: {error}, formState }) => 
                        <MobileDatePicker
                         
                          label="Fecha"
                          value={field.value}
                          
                          componentsProps={{
                            actionBar:{
                              onCancel:()=> {
                               
                                setOpenDatePicker(false);
                               
                              }
                            }
                          }}
                          inputFormat={'DD/MM/YYYY'}
                          open={openDatePicker}
                          onOpen={()=> {
                            setOpenDatePicker(true);
                          }}
                          onAccept={(newValue)=> {
                            prepareData()
                            setOpenDatePicker(false);
                          }}
                          onChange={(newValue) => {
                          setValue('now',newValue);        
                          }}
                          DialogProps={{
                            onClose:(e,reason)=>{}
                          }}
                          InputProps={{
                            endAdornment:(                  
                              <InputAdornment  position="end" >
                                <div>
                                  <IconButton   color="primary" aria-label="upload picture" component="label">                            
                                
                                  <CalendarTodayIcon sx={{fontSize:'25px', color:'black'}} />
                                
                                  </IconButton>      
                                </div>
                                                  
                              </InputAdornment>
                            )
                          }}   
                        className={classes.fileUpload}
                        renderInput={(params) => <TextField {...params}
                        
                        sx={{width:"95%"}}
                        helperText={error ? error.message : null}
                        error={!!error}
                        {...field}
                       />}
                  />
                        }
                  />
                      </Grid>
                      </LocalizationProvider>
                  </ThemeProvider>
            </form> */}
        <Grid item sx={{ marginRight: "40px" }}>
          <div
            style={{
              display: "flex",
              alignContent: "end",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              {currentUser.role == ROLES.SUPER && (
                <Autocomplete
                  id="combo-box-demo"
                  options={businessList}
                  ListboxProps={{
                    style: { maxHeight: "10.8rem", fontSize: "13px" },
                  }}
                  getOptionLabel={(option) => option.razonSocial}
                  clearOnBlur
                  selectOnFocus
                  disableClearable
                  freeSolo
                  onChange={(event, newValue) => {
                    setBusiness(newValue.id);
                  }}
                  noOptionsText="No se han encontrado opciones"
                  forcePopupIcon={true}
                  popupIcon={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "8px 8px 8px 8px",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="9"
                        viewBox="0 0 14 9"
                        fill="none"
                      >
                        <path
                          d="M6.87433 8.99344C6.599 8.96361 6.33864 8.83861 6.12911 8.63587L0.501909 3.21448C0.218731 2.95942 0.0398573 2.58607 0.00584532 2.18C-0.0279863 1.77389 0.0860324 1.36991 0.322107 1.06001C0.557994 0.750123 0.895737 0.561042 1.25782 0.536038C1.62006 0.511037 1.97577 0.652222 2.24378 0.927439L7 5.51126L11.7562 0.927439C12.0243 0.652427 12.38 0.511244 12.7422 0.536248C13.1042 0.561039 13.442 0.750123 13.6779 1.06001C13.914 1.36989 14.028 1.77409 13.9942 2.18021C13.9601 2.58632 13.7813 2.95943 13.4981 3.21448L7.87089 8.63587C7.59557 8.90205 7.23592 9.03105 6.87446 8.99365L6.87433 8.99344Z"
                          fill={darkMode ? "white" : "rgba(0, 0, 0, 0.54)"}
                        />
                      </svg>
                    </div>
                  }
                  value={
                    businessList.length > 0
                      ? businessList.find((b) => b.id == business)
                      : { id: 0, razonSocial: "Todas" }
                  }
                  blurOnSelect={true}
                  sx={{
                    "& label": {
                      color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
                      fontFamily: "Ubuntu",
                      fontStyle: "normal",
                      fontWeight: "400",
                      fontSize: "13px",
                      lineHeight: "24px",
                      letterSpacing: "0.15px",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: darkMode ? "white" : "rgba(0, 0, 0, 0.23)",
                      },
                      "&:hover fieldset": {
                        borderColor: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: darkMode ? "white" : "#1976d2",
                      },
                    },
                    "& .MuiSvgIcon-root": {
                      color: darkMode ? "white" : "rgba(0, 0, 0, 0.54)",
                    },
                    height: "30px",
                    marginBottom: "10px",
                    width: "229px",
                    marginLeft: 6.5,
                  }}
                  renderInput={(params) => (
                    <TextField
                      id="vempresa"
                      {...params}
                      label="Empresa"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          fontSize: "13px",
                          color: darkMode ? "white" : "rgba(0, 0, 0, 0.87)",
                        },
                      }}
                    />
                  )}
                />

                // <Select
                //   id="business-input"
                //   sx={{ width: "219px", height: "30px"}}
                //   className={classes.select}
                //   value={business}
                //   IconComponent={ExpandMoreIcon}
                //   MenuProps={{
                //     style: {
                //       maxHeight: "267px",
                //       marginTop: "0px",
                //       paddingTop: "0px",
                //       maxWidth: "219px",
                //     },
                //   }}
                //   onChange={(e) => {
                //     if (loading) return
                //     setBusiness(e.target.value)
                //   }}
                // >
                //   <MenuItem
                //     key={0}
                //     value={0}
                //     sx={{
                //       color: "black",
                //       fontSize: "13px",
                //       fontWeight: "400",
                //       lineHeight: "24px",
                //       fontFamily: "Ubuntu !important",
                //     }}
                //   >
                //     Todas
                //   </MenuItem>
                //   {businessList.map((b) => (
                //     <MenuItem
                //       key={b.id}
                //       value={b.id}
                //       sx={{
                //         color: "black",
                //         fontSize: "13px",
                //         fontWeight: "400",
                //         lineHeight: "24px",
                //         fontFamily: "Ubuntu !important",
                //       }}
                //     >
                //       {b.razonSocial}
                //     </MenuItem>
                //   ))}
                // </Select>
              )}
              <Typography
                variant="p"
                className={classes.dashboardHeaderSubTitle}
                sx={{
                  paddingLeft: "35px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  justifyContent: "end",
                  paddingRight: "2px",
                }}
              >
                {" "}
                {!darkMode ? "Modo claro" : "Modo oscuro"}{" "}
                <AntSwitch
                  disabled={loading}
                  className={classes.switchButton}
                  sx={{
                    "& .MuiSwitch-track": {
                      backgroundColor: darkMode
                        ? "#1BC3DD !important"
                        : "#1BC3DD !important",
                    },
                  }}
                  onClick={changeMode}
                />{" "}
              </Typography>
            </div>
            <div
              style={{ width: "100%", display: "flex", justifyContent: "end" }}
            >
              <Typography
                variant="h3"
                className={classes.dashboardHeaderTitle}
                sx={{ fontSize: "24px !important" }}
                textTransform={"capitalize"}
              >
                {dateRange.startDate.format("MMMM")}
                <Typography
                  variant="spam"
                  sx={{ fontWeight: "400", fontSize: "20px" }}
                >
                  {" "}
                  {dateRange.startDate.format("YYYY")}{" "}
                </Typography>
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>
      {/* Second Section */}
      <div
        style={{
          display: "flex",
          height: "fit-content",
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          sx={{ alignItems: "stretch" }}
        >
          <Grid
            item
            className={classes.countMessageContainer}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Grid
              item
              container
              direction={"column"}
              className={classes.totalSmsContainer}
              style={{
                border:
                  "1px solid " +
                  (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
                boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
              }}
              sx={{ flexShrink: 0, padding: "20px" }}
            >
              {/* Title Section */}
              <Box
                sx={{
                  textAlign: "center",
                  marginBottom: "10px",
                  position: "relative",
                  top: "-12px",
                  left: "0",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Ubuntu !important",
                    fontWeight: "500 !important",
                    fontSize: "25px !important",
                    color: darkMode ? "white" : "#000",
                    height: "27px",
                  }}
                >
                  {formatNumberShort(
                    dashboardData.totalSuccessful + dashboardData.totalFailed,
                  )}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Ubuntu !important",
                    fontWeight: "400 !important",
                    fontSize: "12px !important",
                    color: darkMode ? "#fff" : "#000",
                  }}
                >
                  Mensajes enviados
                </Typography>
              </Box>

              {/* Gauge Section */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {/* Left Stats - Exitosos */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "-10px",
                    top: "-5px",
                    textAlign: "start",
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu !important",
                      fontWeight: "500",
                      fontSize: "25px",
                      color: "#1BC3DD",
                      height: "27px",
                    }}
                  >
                    {formatNumberShort(dashboardData.totalSuccessful)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu !important",
                      fontWeight: "400",
                      fontSize: "12px",
                      color: "#1BC3DD",
                    }}
                  >
                    Exitosos
                  </Typography>
                </Box>

                {/* Right Stats - Fallidos */}
                <Box
                  sx={{
                    position: "absolute",
                    right: "-10px",
                    top: "-5px",
                    textAlign: "end",
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu !important",
                      fontWeight: "500",
                      fontSize: "25px",
                      color: darkMode ? "#D67A63" : "#9C27B0",
                      height: "27px",
                    }}
                  >
                    {formatNumberShort(dashboardData.totalFailed)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu !important",
                      fontWeight: "400",
                      fontSize: "12px",
                      color: darkMode ? "#D67A63" : "#9C27B0",
                    }}
                  >
                    Fallidos
                  </Typography>
                </Box>

                {/* Gauge Chart */}
                <Box
                  sx={{
                    width: "70%",
                    position: "relative",
                    aspectRatio: "2/1",
                    margin: "0 0 40px 0",
                  }}
                >
                  {/* Background gauge: cyan for success, gray for remaining */}
                  <Doughnut
                    data={{
                      datasets: [
                        {
                          data: (() => {
                            const total =
                              dashboardData.totalSuccessful +
                              dashboardData.totalFailed;
                            const effectiveness =
                              total > 0
                                ? (dashboardData.totalSuccessful * 100) / total
                                : 0;
                            return [effectiveness, 100 - effectiveness];
                          })(),
                          backgroundColor: ["#1BC3DD", "#E2E8F0"],
                          borderWidth: 0,
                          borderRadius: 20,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      rotation: -90,
                      circumference: 180,
                      cutout: windowWidth > 1300 ? "81%" : "75%",
                      plugins: {
                        legend: { display: false },
                        tooltip: { enabled: false },
                        datalabels: { display: false },
                      },
                    }}
                  />
                  {/* Overlay: magenta dotted line centered in the gray area */}
                  {!loading && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: windowWidth > 1300 ? "53%" : "50%",
                        left: "50%",
                        width: windowWidth > 1300 ? "92%" : "89%",
                        height: windowWidth > 1300 ? "100%" : "84%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <Doughnut
                        data={{
                          datasets: [
                            {
                              data: (() => {
                                const total =
                                  dashboardData.totalSuccessful +
                                  dashboardData.totalFailed;
                                const effectiveness =
                                  total > 0
                                    ? (dashboardData.totalSuccessful * 100) /
                                      total
                                    : 0;
                                const failedPortion = 100 - effectiveness;
                                const padding = 2; // padding on each side
                                const dottedPortion = Math.max(
                                  0,
                                  failedPortion - padding * 2,
                                );

                                // Create dotted effect: alternating dots and gaps
                                const dotSize = 6;
                                const gapSize = 2;
                                const segments = [effectiveness + padding]; // Transparent success portion + start padding

                                let remaining = dottedPortion;
                                while (remaining > 0) {
                                  // Add dot
                                  segments.push(Math.min(dotSize, remaining));
                                  remaining -= dotSize;
                                  if (remaining > 0) {
                                    // Add gap
                                    segments.push(Math.min(gapSize, remaining));
                                    remaining -= gapSize;
                                  }
                                }
                                segments.push(1);
                                return segments;
                              })(),
                              backgroundColor: (() => {
                                const total =
                                  dashboardData.totalSuccessful +
                                  dashboardData.totalFailed;
                                const effectiveness =
                                  total > 0
                                    ? (dashboardData.totalSuccessful * 100) /
                                      total
                                    : 0;
                                const failedPortion = 100 - effectiveness;
                                const padding = 0;
                                const dottedPortion = Math.max(
                                  0,
                                  failedPortion - padding * 2,
                                );

                                const dotSize = 6;
                                const gapSize = 2;
                                const colors = ["transparent"]; // Success portion + start padding

                                let remaining = dottedPortion;
                                let isDot = true;
                                while (remaining > 0) {
                                  colors.push(
                                    isDot
                                      ? failedPortion == 100
                                        ? "transparent"
                                        : darkMode
                                          ? "#D67A63"
                                          : "#9C27B0"
                                      : "transparent",
                                  );
                                  remaining -= isDot ? dotSize : gapSize;
                                  isDot = !isDot;
                                }
                                colors.push("transparent"); // End padding
                                return colors;
                              })(),
                              borderWidth: 0,
                              borderRadius: 6,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          events: [],
                          maintainAspectRatio: false,
                          rotation: -90,
                          circumference: 180,
                          cutout: windowWidth > 1300 ? "95%" : "94%",
                          plugins: {
                            legend: { display: false },
                            tooltip: { enabled: false },
                            datalabels: { display: false },
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Scale Labels */}
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: windowWidth > 1500 ? "-12%" : "0",
                      left: "3%",
                      fontFamily: "Ubuntu !important",
                      fontSize: "11px",
                      color: darkMode ? "#BBBBBB" : "#637381",
                    }}
                  >
                    0
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      top: windowWidth > 1500 ? "-17px" : "-5px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontFamily: "Ubuntu !important",
                      fontSize: "11px",
                      color: darkMode ? "#BBBBBB" : "#637381",
                    }}
                  >
                    50
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: windowWidth > 1500 ? "-12%" : "0",
                      right: "2%",
                      fontFamily: "Ubuntu !important",
                      fontSize: "11px",
                      color: darkMode ? "#BBBBBB" : "#637381",
                    }}
                  >
                    100
                  </Typography>

                  {/* Needle - Triangle pointer (pointing up, rotates from bottom) */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom:
                        windowWidth >= 1500
                          ? windowWidth >= 1800
                            ? "0px"
                            : "10px"
                          : windowWidth > 1300
                            ? "20px"
                            : "30px",
                      left: "50%",
                      width: "8px",
                      height:
                        windowWidth >= 1500
                          ? windowWidth >= 1800
                            ? "135px"
                            : "100px"
                          : windowWidth > 1300
                            ? "80px"
                            : "70px",
                      transformOrigin: "center bottom",
                      transform: `translateX(-50%) rotate(${
                        -90 +
                        Math.min(
                          (dashboardData.totalSuccessful * 100) /
                            (dashboardData.totalSuccessful +
                              dashboardData.totalFailed) || 0,
                          100,
                        ) *
                          1.8
                      }deg)`,
                      zIndex: 2,
                    }}
                  >
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 8 90"
                      preserveAspectRatio="none"
                    >
                      <polygon
                        points="4,0 1,90 7,90"
                        fill={darkMode ? "#D67A63" : "#334155"}
                      />
                    </svg>
                  </Box>

                  {/* Needle Center Circle */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom:
                        windowWidth >= 1500
                          ? windowWidth >= 1800
                            ? "-5px"
                            : "5px"
                          : windowWidth > 1300
                            ? "15px"
                            : "24px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "11px",
                      height: "11px",
                      backgroundColor: darkMode ? "#D67A63" : "#334155",
                      borderRadius: "50%",
                      zIndex: 3,
                    }}
                  />

                  {/* Effectiveness Text - Centered in gauge */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom:
                        windowWidth >= 1500
                          ? "-30%"
                          : windowWidth > 1300
                            ? "-20%"
                            : "-12%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu !important",
                        fontWeight: "500",
                        fontSize: "22px",
                        color: darkMode ? "white" : "#000",
                        opacity: 0.5,
                      }}
                    >
                      {(
                        (dashboardData.totalSuccessful * 100) /
                          (dashboardData.totalSuccessful +
                            dashboardData.totalFailed) || 0
                      ).toFixed(0)}
                      %
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu !important",
                        fontSize: "8px",
                        fontWeight: "500",
                        color: darkMode ? "white" : "#000",
                        opacity: 0.5,
                      }}
                    >
                      Efectividad
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              container
              direction={"column"}
              sx={{ marginTop: "15px", position: "relative", flex: 1 }}
              className={classes.carrierGraphicContainer}
              style={{ boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)" }}
            >
              <Grid
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "92%",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <Typography
                  variant="p"
                  className={classes.dashboardHeaderSubTitle}
                  sx={{
                    fontWeight: "500 !important",
                    marginLeft: "5px",
                    color: "black",
                    fontSize: "18px !important",
                  }}
                >
                  {" "}
                  Envío por operadora{" "}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 300,
                    fontSize: "13px",
                    color: darkMode ? "white" : "black",
                    fontFamily: "Ubuntu !important",
                  }}
                  variant="spam"
                >
                  Por mes
                </Typography>
              </Grid>

              <Grid
                item
                container
                direction={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                sx={{
                  width: "100%",
                  minHeight: "200px",
                  marginLeft: "8%",
                  marginTop: "10px",
                }}
              >
                {loading === false && pieGraphic && !noData ? (
                  <Grid
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: { xs: 2, md: 3 },
                      px: { xs: 0, md: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "100%", md: "62%" },
                        maxWidth: "360px",
                        aspectRatio: "1 / 1",
                        position: "relative",
                      }}
                    >
                      <Doughnut
                        data={{
                          labels: ["Movistar", "Digitel", "Movilnet"],
                          datasets: [
                            {
                              data: [
                                movistarAmount,
                                digitelAmount,
                                movilnetAmount,
                              ],
                              backgroundColor: [
                                "#009EF7",
                                "#E0003F",
                                "#F05A5D",
                              ],
                              borderWidth: 0,
                              hoverBorderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          rotation: 90,
                          cutout: "74%",
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              enabled: true,
                              backgroundColor: !darkMode ? "white" : "#4E4E4E",
                              bodyColor: !darkMode ? "#000" : "white",
                              borderColor: !darkMode ? "#1BC3DD" : "#1BC3DD",
                              borderWidth: 1,
                              titleColor: !darkMode ? "#000" : "white",
                              displayColors: false,
                            },
                            datalabels: { display: false },
                          },
                        }}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontFamily: "Ubuntu !important",
                              fontWeight: "500 !important",
                              fontSize: "32px",
                              color: darkMode
                                ? "rgba(255, 255, 255, 0.70)"
                                : "rgba(0, 0, 0, 0.70)",
                              lineHeight: 1,
                            }}
                          >
                            {movistarPercent}%
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Ubuntu !important",
                              fontWeight: 300,
                              fontSize: "15px",
                              color: darkMode ? "#fff" : "#000",
                              lineHeight: 1.2,
                            }}
                          >
                            {formatCarrierAmount(movistarAmount)}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Ubuntu !important",
                              fontWeight: 400,
                              fontSize: "15px",
                              color: darkMode ? "white" : "#000",
                              lineHeight: 1.2,
                            }}
                          >
                            Movistar
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        width: { xs: "100%", md: "100%" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-evenly",
                        marginRight: "5%",
                        gap: "6vw",
                        alignItems: "center",
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu !important",
                            fontWeight: "500 !important",
                            fontSize: "25px",
                            color: darkMode
                              ? "rgba(255, 255, 255, 0.70)"
                              : "rgba(0, 0, 0, 0.70)",
                            lineHeight: 1,
                          }}
                        >
                          {digitelPercent}%
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu !important",
                            fontWeight: 400,
                            fontSize: "15px",
                            color: darkMode ? "white" : "#111111",
                            lineHeight: 1.2,
                          }}
                        >
                          Digitel
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu !important",
                            fontWeight: 300,
                            fontSize: "15px",
                            color: darkMode ? "white" : "#111111",
                            lineHeight: 1.2,
                          }}
                        >
                          {formatCarrierAmount(digitelAmount)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu !important",
                            fontWeight: "500 !important",
                            fontSize: "25px",
                            color: darkMode
                              ? "rgba(255, 255, 255, 0.70)"
                              : "rgba(0, 0, 0, 0.70)",
                            lineHeight: 1,
                          }}
                        >
                          {movilnetPercent}%
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu !important",
                            fontWeight: 400,
                            fontSize: "15px",
                            color: darkMode ? "white" : "#111111",
                            lineHeight: 1.2,
                          }}
                        >
                          Movilnet
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu !important",
                            fontWeight: 300,
                            fontSize: "15px",
                            color: darkMode ? "white" : "#111111",
                            lineHeight: 1.2,
                          }}
                        >
                          {formatCarrierAmount(movilnetAmount)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ) : (
                  <Grid
                    sx={{
                      marginTop: "20px",
                      width: "100%",
                      minHeight: "150px",
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    {loading ? (
                      <CircularProgress
                        sx={{
                          color: "#4E5589",
                          position: "absolute",
                          top: "40%",
                          right: "48%",
                        }}
                        size={50}
                        variant="indeterminate"
                      />
                    ) : (
                      <React.Fragment>
                        <Typography
                          sx={{
                            fontSize: "14px !important",
                            color: darkMode ? "white" : "#0000004D",
                            paddingLeft: "10px",
                          }}
                        >
                          0%
                        </Typography>
                        <img
                          src={
                            darkMode
                              ? GraphicNoDataImg.NoDataPieDark
                              : GraphicNoDataImg.NoDataPieWhite
                          }
                          style={{ width: "50%" }}
                          alt=""
                        />
                      </React.Fragment>
                    )}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            className={classes.graphicLinearContainer}
            style={{
              border:
                "1px solid " +
                (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
              boxShadow: !darkMode
                ? ""
                : "0 2px 8px 0 rgba(255, 255, 255, 0.10)",
            }}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
              maxHeight: "100%",
            }}
          >
            <Grid
              item
              container
              direction={"column"}
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* Header - Title and Buttons - Always at top */}
              <Grid
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Grid
                  sx={{ display: "flex", flexDirection: "column", gap: "3px" }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: 24,
                      fontFamily: "Ubuntu !important",
                    }}
                  >
                    {showCampaignTypeChart
                      ? "Campañas normales/personalizadas"
                      : "Envío sms por producto"}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 300,
                      fontSize: "16px",
                      color: darkMode ? "white" : "#637381",
                      fontFamily: "Public Sans !important",
                    }}
                  >
                    {showCampaignTypeChart
                      ? "Relación entre tipos de campañas"
                      : "Volumen de envio de mensajes"}
                  </Typography>
                </Grid>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Button
                    onClick={() => setShowCampaignTypeChart(false)}
                    sx={{
                      fontFamily: "Ubuntu !important",
                      textTransform: "none",
                      backgroundColor: !showCampaignTypeChart
                        ? "#1BC3DD"
                        : "transparent",
                      color: !showCampaignTypeChart
                        ? "#FFFFFF"
                        : darkMode
                          ? "#FFFFFF"
                          : "#171D1E",
                      border:
                        "1px solid " +
                        (!showCampaignTypeChart ? "#1BC3DD" : "#E0E0E0"),
                      borderRadius: "10px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      fontWeight: "500",
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: !showCampaignTypeChart
                          ? "#00ACC1"
                          : darkMode
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(78,85,137,0.1)",
                      },
                    }}
                  >
                    Envío SMS
                  </Button>
                  <Button
                    onClick={() => setShowCampaignTypeChart(true)}
                    sx={{
                      fontFamily: "Ubuntu !important",
                      textTransform: "none",
                      backgroundColor: showCampaignTypeChart
                        ? "#1BC3DD"
                        : "transparent",
                      color: showCampaignTypeChart
                        ? "#FFFFFF"
                        : darkMode
                          ? "#FFFFFF"
                          : "#171D1E",
                      border:
                        "1px solid " +
                        (showCampaignTypeChart ? "#1BC3DD" : "#E0E0E0"),
                      borderRadius: "10px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      fontWeight: "500",
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: showCampaignTypeChart
                          ? "#00ACC1"
                          : darkMode
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(78,85,137,0.1)",
                      },
                    }}
                  >
                    Campañas normales/personalizadas
                  </Button>
                </Box>
              </Grid>
              {/* Chart Content Area - Fills remaining space */}
              <Grid
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  marginTop: "20px",
                }}
              >
                {loading === false && linearGraphic && !noData ? (
                  showCampaignTypeChart ? (
                    // Campaign Type Circular Chart
                    <Grid
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                      }}
                    >
                      {campaignTypeGraphic && (
                        <>
                          {/* Chart centered in available space */}
                          <Grid
                            sx={{
                              flex: 1,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              minHeight: "300px",
                            }}
                          >
                            <Grid sx={{ width: "100%", maxWidth: "480px" }}>
                              {dashboardData.totalCampaignType?.normal > 0 ||
                              dashboardData.totalCampaignType?.custom > 0 ? (
                                <Doughnut
                                  data={campaignTypeGraphic}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: true,
                                    aspectRatio: 1.2,
                                    radius: "80%",
                                    cutout: "65%",
                                    spacing: 0,
                                    interaction: {
                                      mode: null,
                                    },
                                    plugins: {
                                      legend: {
                                        display: false,
                                      },
                                      tooltip: {
                                        enabled: false,
                                      },
                                      datalabels: {
                                        labels: {
                                          index: {
                                            align: "end",
                                            anchor: "end",
                                            color: function (context) {
                                              return context.dataset
                                                .backgroundColor[
                                                context.dataIndex
                                              ];
                                            },
                                            font: {
                                              size: 18,
                                              weight: "bold",
                                              family: "Ubuntu",
                                            },
                                            formatter: function (value, ctx) {
                                              const total =
                                                (dashboardData.totalCampaignType
                                                  ?.normal || 0) +
                                                (dashboardData.totalCampaignType
                                                  ?.custom || 0);
                                              if (total === 0) return "0%";
                                              return `${((value * 100) / total).toFixed(1)}%`;
                                            },
                                            offset: 8,
                                          },
                                        },
                                      },
                                    },
                                  }}
                                  plugins={[ChartDataLabels]}
                                />
                              ) : (
                                <Grid>
                                  <img
                                    src={
                                      darkMode
                                        ? GraphicNoDataImg.NoDataLineDark
                                        : GraphicNoDataImg.NoDataLineWhite
                                    }
                                    style={{ width: "100%" }}
                                    alt=""
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "14px !important",
                                      position: "absolute",
                                      bottom: "30%",
                                      right: "35%",
                                      color: darkMode ? "white" : "#00000080",
                                    }}
                                  >
                                    El gráfico aún no se encuentra disponible
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                          {/* Legend always at bottom */}
                          <Grid
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              gap: "40px",
                              paddingY: "16px",
                              flexShrink: 0,
                            }}
                          >
                            {campaignTypeGraphic.labels.map((label, i) => (
                              <Grid
                                key={i}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "14px",
                                    height: "24px",
                                    backgroundColor:
                                      campaignTypeGraphic.datasets[0]
                                        .backgroundColor[i],
                                    borderRadius: "30px",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontFamily: "Ubuntu",
                                    fontSize: "16px",
                                    fontWeight: "400",
                                    color:
                                      campaignTypeGraphic.datasets[0]
                                        .backgroundColor[i],
                                  }}
                                >
                                  {label}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  ) : (
                    // Original Line Chart
                    <Grid sx={{ width: "100%", marginTop: "30px" }}>
                      <Line
                        id="lines"
                        options={GetLineGraphicOptions(
                          darkMode,
                          dateRange,
                          linearGraphic,
                        )}
                        data={linearGraphic}
                        plugins={[htmlLegendPlugin]}
                      />
                      {/* Legend at bottom */}
                      <Grid
                        item
                        sx={{
                          display: "flex",
                          justifyContent: "end",
                          flexShrink: 0,
                        }}
                      >
                        <Grid ref={legendMenuRef} sx={{ position: "relative" }}>
                          <IconButton
                            type="button"
                            id="menu"
                            onClick={handleClick}
                            sx={{
                              color: darkMode ? "white" : "#4E5589",
                              transform: "translateY(0px)",
                            }}
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>

                          <Grid
                            id="js-legend"
                            sx={{ display: openLegend ? "inline" : "none" }}
                            className={"legend-css " + classes.legendMenu}
                          >
                            <FormGroup>
                              {lineGraphicExternalLegend.map((line, index) => {
                                return (
                                  <Grid
                                    key={index}
                                    item
                                    container
                                    direction={"row"}
                                    alignItems={"flex-end"}
                                    sx={{ width: "100%" }}
                                  >
                                    <FormControlLabel
                                      sx={{
                                        "& .MuiTypography-root ": {
                                          fontSize: "13px !important",
                                          fontFamily: "Ubuntu !important",
                                          marginLeft: "3px",
                                        },
                                      }}
                                      control={
                                        <Checkbox
                                          onClick={() => {
                                            handleOption(`li-${line.id}`);
                                          }}
                                          sx={{
                                            paddingRight: "3px !important",
                                            paddingBottom: "2px !important",
                                            paddingTop: "2px !important",
                                            color: line.color,
                                            "&.Mui-checked": {
                                              color: line.color,
                                            },

                                            "& .MuiSvgIcon-root": {
                                              fontSize: 16,
                                            },
                                          }}
                                          defaultChecked
                                        />
                                      }
                                      label={line.label}
                                    />
                                  </Grid>
                                );
                              })}
                            </FormGroup>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  )
                ) : (
                  <Grid sx={{ width: "100%", marginTop: "90px" }}>
                    {loading && (
                      <CircularProgress
                        sx={{
                          color: "#4E5589",
                          position: "absolute",
                          top: "25%",
                          right: "48%",
                        }}
                        size={50}
                        variant="indeterminate"
                      />
                    )}
                    <img
                      src={
                        darkMode
                          ? GraphicNoDataImg.NoDataLineDark
                          : GraphicNoDataImg.NoDataLineWhite
                      }
                      style={{ width: "100%" }}
                      alt=""
                    />
                    <Typography
                      sx={{
                        fontSize: "14px !important",
                        position: "absolute",
                        bottom: "30%",
                        right: "35%",
                        color: darkMode ? "white" : "#00000080",
                      }}
                    >
                      El gráfico aún no se encuentra disponible
                    </Typography>

                    <Grid
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginLeft: "10%",
                        color: darkMode ? "white" : "#60789D",
                      }}
                    >
                      <Typography sx={{ fontSize: "14px !important" }}>
                        0
                      </Typography>
                      <Typography sx={{ fontSize: "14px !important" }}>
                        5
                      </Typography>
                      <Typography sx={{ fontSize: "14px !important" }}>
                        10
                      </Typography>
                      <Typography sx={{ fontSize: "14px !important" }}>
                        15
                      </Typography>
                      <Typography sx={{ fontSize: "14px !important" }}>
                        20
                      </Typography>
                      <Typography sx={{ fontSize: "14px !important" }}>
                        25
                      </Typography>
                      <IconButton
                        type="button"
                        id="menu"
                        sx={{ color: darkMode ? "white" : "#4E5589" }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <Grid item container>
        <Grid
          item
          container
          direction={"column"}
          className={classes.messageByProductContainer}
          sx={{ height: "500px" }}
        >
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"space-between"}
            sx={{ height: "100%" }}
          >
            <Grid
              item
              container
              direction={"column"}
              justifyContent={"flex-start"}
              sx={{
                width: { xs: "100%", lg: "65%" },
                height: "100%",
                paddingRight: { lg: "18px" },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  height: "100%",
                  border:
                    "1px solid " +
                    (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
                  boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
                  padding: { xs: "20px", md: "24px" },
                }}
              >
                <Grid
                  container
                  justifyContent="flex-start"
                  alignItems="center"
                  height="10%"
                  sx={{ gap: 2, marginBottom: "16px" }}
                >
                  <Grid
                    item
                    sx={{
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "12px",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: "100%", sm: "fit-content" },
                          maxWidth: "100%",
                        }}
                      >
                        <TextField
                          label=""
                          type="text"
                          size="small"
                          value={productAnalyticsDateRangeLabel}
                          onClick={handleOpenProductAnalyticsRangePicker}
                          onChange={() => {}}
                          inputProps={{
                            title: productAnalyticsDateRangeLabel,
                          }}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <ExpandMoreIcon
                                  sx={{
                                    color: darkMode ? "#fff" : "#4E5589",
                                    fontSize: "18px",
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            cursor: "pointer",
                            width: { xs: "80%", md: "280px", xl: "360px" },
                            "& .MuiOutlinedInput-root": {
                              width: "100%",
                              background: darkMode ? "#131313" : "white",
                              borderRadius: "14px",
                              color: darkMode ? "white" : "#0F172A",
                              border: darkMode
                                ? "1px solid white"
                                : "1px solid #E0E0E0",
                            },
                            "& .MuiOutlinedInput-input": {
                              cursor: "pointer",
                              fontSize: "14px",
                              padding: "10px 12px",
                              fontFamily: "Open Sans !important",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "14px",
                              color: darkMode ? "#CBD5E1" : "#64748B",
                              fontFamily: "Ubuntu !important",
                            },
                          }}
                        />
                      </Box>
                      <Button
                        onClick={() =>
                          setIsProductAnalyticsComparing((prev) => !prev)
                        }
                        sx={{
                          minWidth: "96px",
                          marginLeft: "auto",
                          padding: "6px 12px",
                          borderRadius: "10px",
                          background: "#1BC3DD",
                          color: "white",
                          textTransform: "none",
                          fontSize: "14px",
                          fontWeight: 700,
                          fontFamily: "Open Sans !important",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            background: "#1fabc0",
                          },
                        }}
                      >
                        {isProductAnalyticsComparing
                          ? "Dejar de comparar"
                          : "Comparar con el período anterior"}
                      </Button>
                    </Box>
                    <ThemeProvider theme={theme}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Dialog
                          open={productAnalyticsRangePicker.open}
                          onClose={handleCloseProductAnalyticsRangePicker}
                          PaperProps={{
                            sx: {
                              borderRadius: "18px",
                              overflow: "hidden",
                            },
                          }}
                        >
                          <DialogContent sx={{ px: 2, pt: 2, pb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#334155",
                                fontFamily: "Open Sans !important",
                                marginBottom: "8px",
                              }}
                            >
                              {productAnalyticsRangePicker.selectingEnd
                                ? "Selecciona la fecha final"
                                : "Selecciona la fecha inicial"}
                            </Typography>
                            <StaticDatePicker
                              displayStaticWrapperAs="desktop"
                              inputFormat="DD/MM/YYYY"
                              value={
                                productAnalyticsRangePicker.selectingEnd
                                  ? productAnalyticsDraftRange.endDate
                                  : productAnalyticsDraftRange.startDate
                              }
                              minDate={productAnalyticsMinSelectableDate}
                              maxDate={productAnalyticsMaxSelectableDate}
                              showToolbar={false}
                              onChange={handleProductAnalyticsRangeDateChange}
                              renderDay={renderProductAnalyticsRangeDay}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  sx={{ display: "none" }}
                                />
                              )}
                            />
                          </DialogContent>
                          <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button
                              onClick={handleCloseProductAnalyticsRangePicker}
                            >
                              CANCELAR
                            </Button>
                            <Button
                              onClick={handleApplyProductAnalyticsRangePicker}
                            >
                              OK
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </LocalizationProvider>
                    </ThemeProvider>
                  </Grid>
                </Grid>

                <Grid
                  container
                  spacing={1.5}
                  sx={{ marginBottom: "16px", height: "20%" }}
                >
                  {productAnalyticsSummaryCards.map((card) => (
                    <Grid item xs={12} md={4} key={card.title}>
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: "16px",
                          padding: "14px 16px",
                          background: darkMode ? "#131313" : "white",
                          border: " 1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 400,
                            color: darkMode ? "#CBD5E1" : "#637381",
                            fontFamily: "Ubuntu !important",
                          }}
                        >
                          {card.title}
                        </Typography>
                        <Box
                          sx={{
                            marginTop: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0px",
                            flexWrap: "no-wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "20px",
                              lineHeight: 1.1,
                              fontWeight: "700 !important",
                              color: darkMode ? "white" : "#171D1E",
                              fontFamily: "Ubuntu !important",
                            }}
                          >
                            {card.value}
                          </Typography>
                          {typeof card.variation === "number" && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "2px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  lineHeight: 1,
                                  fontWeight: 400,
                                  color:
                                    card.variation >= 0 ? "#1BC3DD" : "#EF4444",
                                  fontFamily: "Ubuntu !important",
                                }}
                              >
                                {card.variation >= 0 ? (
                                  <ArrowUpward />
                                ) : (
                                  <ArrowDownward />
                                )}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color:
                                    card.variation >= 0 ? "#1BC3DD" : "#EF4444",
                                  fontFamily: "Ubuntu !important",
                                }}
                              >
                                {formatVariationLabel(card.variation)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ height: "58%" }}>
                  <Bar
                    data={productAnalyticsBarData}
                    options={productAnalyticsBarOptions}
                  />
                </Box>
                <Box
                  sx={{
                    marginTop: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "18px",
                    flexWrap: "wrap",
                    height: "12%",
                  }}
                >
                  {isProductAnalyticsComparing && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="7"
                        viewBox="0 0 14 7"
                        fill="none"
                      >
                        {darkMode ? (
                          <path
                            d="M0 3.20833H4.66667M4.66667 3.20833C4.66667 2.58949 4.9125 1.996 5.35008 1.55842C5.78767 1.12083 6.38116 0.875 7 0.875C7.61884 0.875 8.21233 1.12083 8.64992 1.55842C9.0875 1.996 9.33333 2.58949 9.33333 3.20833M4.66667 3.20833C4.66667 3.82717 4.9125 4.42066 5.35008 4.85825C5.78767 5.29583 6.38116 5.54167 7 5.54167C7.61884 5.54167 8.21233 5.29583 8.64992 4.85825C9.0875 4.42066 9.33333 3.82717 9.33333 3.20833M9.33333 3.20833H14"
                            stroke="#D9CB67"
                            strokeWidth="1.75"
                          />
                        ) : (
                          <path
                            d="M0 3.20833H4.66667M4.66667 3.20833C4.66667 2.58949 4.9125 1.996 5.35008 1.55842C5.78767 1.12083 6.38116 0.875 7 0.875C7.61884 0.875 8.21233 1.12083 8.64992 1.55842C9.0875 1.996 9.33333 2.58949 9.33333 3.20833M4.66667 3.20833C4.66667 3.82717 4.9125 4.42066 5.35008 4.85825C5.78767 5.29583 6.38116 5.54167 7 5.54167C7.61884 5.54167 8.21233 5.29583 8.64992 4.85825C9.0875 4.42066 9.33333 3.82717 9.33333 3.20833M9.33333 3.20833H14"
                            stroke="#1BC3DD"
                            strokeWidth="1.75"
                          />
                        )}
                      </svg>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 400,
                          fontFamily: "Inter !important",
                          color: darkMode
                            ? "#D9CB67"
                            : productAnalyticsPreviousPeriodColor,
                        }}
                      >
                        Periodo anterior
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="7"
                      viewBox="0 0 14 7"
                      fill="none"
                    >
                      <path
                        d="M0 3.20833H4.66667M4.66667 3.20833C4.66667 2.58949 4.9125 1.996 5.35008 1.55842C5.78767 1.12083 6.38116 0.875 7 0.875C7.61884 0.875 8.21233 1.12083 8.64992 1.55842C9.0875 1.996 9.33333 2.58949 9.33333 3.20833M4.66667 3.20833C4.66667 3.82717 4.9125 4.42066 5.35008 4.85825C5.78767 5.29583 6.38116 5.54167 7 5.54167C7.61884 5.54167 8.21233 5.29583 8.64992 4.85825C9.0875 4.42066 9.33333 3.82717 9.33333 3.20833M9.33333 3.20833H14"
                        stroke="#D67A63"
                        strokeWidth="1.75"
                      />
                    </svg>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 400,
                        fontFamily: "Inter !important",
                        color: productAnalyticsCurrentPeriodColor,
                      }}
                    >
                      Periodo actual
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              container
              direction={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              sx={{ width: { xs: "100%", lg: "35%" }, height: "100%" }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "12px",
                  border:
                    "1px solid " +
                    (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
                  boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
                  padding: { xs: "16px", md: "20px" },
                }}
              >
                <Grid
                  item
                  sx={{
                    width: "100%",
                    height: "5%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    marginBottom: "12px",
                  }}
                >
                  <Box
                    sx={{
                      width: "calc(50% + 57px)",
                      display: "flex",
                      flexDirection: "row",
                      gap: "40px",
                    }}
                  >
                    <Typography
                      variant="h2"
                      className={classes.dashboardHeaderSubTitle}
                      sx={{
                        fontWeight: "400 !important",
                        color: darkMode ? "white" : "#0F172A",
                        fontSize: "15px !important",
                        textAlign: "left",
                      }}
                    >
                      Total acumulado
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 300,
                        color: darkMode ? "#CBD5E1" : "#64748B",
                        fontFamily: "Ubuntu !important",
                      }}
                    >
                      Por mes
                    </Typography>
                  </Box>
                </Grid>
                <Box
                  sx={{
                    height: windowWidth > 1600 ? "95%" : "calc(95% - 30px)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: windowWidth > 1600 ? "center" : "start",
                    paddingTop: windowWidth > 1600 ? "0" : "30px",
                  }}
                >
                  {loading === false && doughnutGraphic && !noData ? (
                    <Grid sx={{ width: "85%" }}>
                      <Doughnut
                        id="chartDoughnut"
                        data={doughnutGraphic}
                        options={GetDoughnutGraphicOptions(
                          darkMode,
                          dashboardData,
                          windowWidth,
                        )}
                        plugins={[textCenter]}
                      />
                    </Grid>
                  ) : (
                    <Grid
                      sx={{
                        width: "85%",
                        minHeight: "90%",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        marginTop: "5px",
                      }}
                    >
                      {loading === false && (
                        <React.Fragment>
                          <img
                            src={
                              darkMode
                                ? GraphicNoDataImg.NoDataDoughnutDark
                                : GraphicNoDataImg.NoDataDoughnutWhite
                            }
                            style={{ width: "80%" }}
                            alt=""
                          />
                          <Grid
                            sx={{
                              position: "absolute",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              height: "20%",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "40px",
                                fontWeight: 700,
                                lineHeight: "40px",
                                color: darkMode ? "white" : "#000",
                              }}
                            >
                              0
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 300,
                                fontSize: "16px",
                                color: darkMode ? "white" : "#000",
                              }}
                            >
                              enviados
                            </Typography>
                          </Grid>
                          <Grid
                            sx={{
                              display: "flex",
                              gap: "20px",
                              paddingTop: "20px",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "11px",
                                color: darkMode ? "white" : "#000",
                              }}
                            >
                              <div
                                style={{
                                  width: "11px",
                                  height: "11px",
                                  background: "#D9D9D9",
                                  marginRight: "5px",
                                }}
                              ></div>{" "}
                              sms exitosos
                            </Typography>
                            <Typography
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "11px",
                                color: darkMode ? "white" : "#000",
                              }}
                            >
                              <div
                                style={{
                                  width: "11px",
                                  height: "11px",
                                  background: "#D9D9D9",
                                  marginRight: "5px",
                                }}
                              ></div>{" "}
                              sms fallidos
                            </Typography>
                          </Grid>
                        </React.Fragment>
                      )}
                    </Grid>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default Dashboard;
